// src/pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Avatar,
  Stack,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  People as PeopleIcon,
  Notifications as NotificationsIcon,
  School as SchoolIcon,
  ShoppingCart as ShoppingCartIcon,
  Receipt as ReceiptIcon,
  Event as EventIcon,
  Poll as PollIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  ChildCare as ChildCareIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  QuestionAnswer as QuestionAnswerIcon
} from '@mui/icons-material';
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';

// Chart components
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

const SECTION_COLORS = {
  users:         '#1565c0',
  children:      '#283593',
  events:        '#6a1b9a',
  notifications: '#bf360c',
  products:      '#00695c',
  orders:        '#004d40',
  services:      '#1b5e20',
  surveys:       '#880e4f',
};

// Mantener COLORS para compatibilidad con charts
const COLORS = {
  primary: '#A8E6CF',
  secondary: '#88D8A3',
  accent: '#68C683',
  success: '#4CAF50',
  warning: '#F5A623',
  error: '#D0021B',
  info: '#4A90E2',
  neutral: '#F8F9FA',
  text: '#2C3E50',
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    usersCount: 0,
    childrenCount: 0,
    notificationsCount: 0,
    servicesCount: 0,
    productsCount: 0,
    ordersCount: 0,
    eventsCount: 0,
    surveysCount: 0,
    recentNotifications: [],
    recentOrders: [],
    recentEvents: [],
    recentSurveys: [],
    productCategories: {
      uniforme: 0,
      material: 0,
      libros: 0,
      otros: 0
    },
    serviceEnrollments: [],
    eventCategories: {
      general: 0,
      cultural: 0,
      deportivo: 0,
      educativo: 0,
      familiar: 0,
      festivo: 0
    },
    surveyResponses: 0,
    activeInactiveSurveys: {
      active: 0,
      inactive: 0
    }
  });

  useEffect(() => {
    let isMounted = true;

    const fetchDashboardData = async () => {
      try {
        // Fetch counts with error handling
        const collections = [
          { name: 'users', key: 'usersCount' },
          { name: 'children', key: 'childrenCount' },
          { name: 'notifications', key: 'notificationsCount' },
          { name: 'services', key: 'servicesCount' },
          { name: 'products', key: 'productsCount' },
          { name: 'orders', key: 'ordersCount' },
          { name: 'events', key: 'eventsCount' },
          { name: 'surveys', key: 'surveysCount' }
        ];

        const counts = {};
        
        for (const { name, key } of collections) {
          try {
            const snapshot = await getDocs(collection(db, name));
            counts[key] = snapshot.size;
          } catch (error) {
            console.error(`Error fetching ${name}:`, error);
            counts[key] = 0;
          }
        }

        if (!isMounted) return;

        // Fetch events data
        const eventsData = await fetchEventsData();
        const notificationsData = await fetchRecentNotifications();
        const ordersData = await fetchRecentOrders();
        const productsData = await fetchProductCategories();
        const servicesData = await fetchServiceEnrollments();
        const surveysData = await fetchSurveysData();

        if (isMounted) {
          setStats({
            ...counts,
            ...eventsData,
            ...notificationsData,
            ...ordersData,
            ...productsData,
            ...servicesData,
            ...surveysData
          });
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchDashboardData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Helper functions for data fetching
  const fetchEventsData = async () => {
    try {
      const eventsSnapshot = await getDocs(collection(db, 'events'));
      
      const eventCategories = {
        general: 0,
        cultural: 0,
        deportivo: 0,
        educativo: 0,
        familiar: 0,
        festivo: 0
      };

      eventsSnapshot.forEach(doc => {
        const event = doc.data();
        const category = event.categoria || 'general';
        if (eventCategories.hasOwnProperty(category)) {
          eventCategories[category]++;
        } else {
          eventCategories.general++;
        }
      });

      const recentEventsQuery = query(
        collection(db, 'events'),
        orderBy('fechaInicio', 'desc'),
        limit(5)
      );
      const recentEventsSnapshot = await getDocs(recentEventsQuery);
      const recentEvents = [];
      recentEventsSnapshot.forEach(doc => {
        recentEvents.push({ id: doc.id, ...doc.data() });
      });

      return { eventCategories, recentEvents };
    } catch (error) {
      console.error('Error fetching events data:', error);
      return { eventCategories: {}, recentEvents: [] };
    }
  };

  const fetchRecentNotifications = async () => {
    try {
      const recentNotificationsQuery = query(
        collection(db, 'notifications'),
        orderBy('fecha', 'desc'),
        limit(5)
      );
      const recentNotificationsSnapshot = await getDocs(recentNotificationsQuery);
      const recentNotifications = [];
      recentNotificationsSnapshot.forEach(doc => {
        recentNotifications.push({ id: doc.id, ...doc.data() });
      });
      return { recentNotifications };
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return { recentNotifications: [] };
    }
  };

  const fetchRecentOrders = async () => {
    try {
      const recentOrdersQuery = query(
        collection(db, 'orders'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      const recentOrdersSnapshot = await getDocs(recentOrdersQuery);
      const recentOrders = [];
      recentOrdersSnapshot.forEach(doc => {
        recentOrders.push({ id: doc.id, ...doc.data() });
      });
      return { recentOrders };
    } catch (error) {
      console.error('Error fetching orders:', error);
      return { recentOrders: [] };
    }
  };

  const fetchProductCategories = async () => {
    try {
      const productsSnapshot = await getDocs(collection(db, 'products'));
      const productCategories = {
        uniforme: 0,
        material: 0,
        libros: 0,
        otros: 0
      };

      productsSnapshot.forEach(doc => {
        const product = doc.data();
        const category = product.categoría || 'otros';
        if (productCategories.hasOwnProperty(category)) {
          productCategories[category]++;
        } else {
          productCategories.otros++;
        }
      });

      return { productCategories };
    } catch (error) {
      console.error('Error fetching product categories:', error);
      return { productCategories: {} };
    }
  };

  const fetchServiceEnrollments = async () => {
    try {
      const serviceEnrollmentsQuery = query(collection(db, 'serviceEnrollments'));
      const serviceEnrollmentsSnapshot = await getDocs(serviceEnrollmentsQuery);
      
      const enrollmentsByService = {};
      for (const docSnapshot of serviceEnrollmentsSnapshot.docs) {
        const enrollment = docSnapshot.data();
        if (!enrollmentsByService[enrollment.serviceId]) {
          enrollmentsByService[enrollment.serviceId] = 0;
        }
        enrollmentsByService[enrollment.serviceId]++;
      }

      const serviceEnrollments = [];
      for (const [serviceId, count] of Object.entries(enrollmentsByService)) {
        try {
          const serviceDoc = await getDoc(doc(db, 'services', serviceId));
          if (serviceDoc.exists()) {
            const service = serviceDoc.data();
            serviceEnrollments.push({
              serviceId,
              name: service.nombre || 'Servicio sin nombre',
              count
            });
          }
        } catch (error) {
          console.error(`Error getting service ${serviceId}:`, error);
        }
      }

      return { serviceEnrollments };
    } catch (error) {
      console.error('Error fetching service enrollments:', error);
      return { serviceEnrollments: [] };
    }
  };

  const fetchSurveysData = async () => {
    try {
      const surveysSnapshot = await getDocs(collection(db, 'surveys'));
      
      const activeInactiveSurveys = { active: 0, inactive: 0 };
      surveysSnapshot.forEach(doc => {
        const survey = doc.data();
        if (survey.active) {
          activeInactiveSurveys.active++;
        } else {
          activeInactiveSurveys.inactive++;
        }
      });

      const recentSurveysQuery = query(
        collection(db, 'surveys'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      const recentSurveysSnapshot = await getDocs(recentSurveysQuery);
      const recentSurveys = [];
      recentSurveysSnapshot.forEach(doc => {
        recentSurveys.push({ id: doc.id, ...doc.data() });
      });

      const surveyResponsesSnapshot = await getDocs(collection(db, 'surveyResponses'));
      const surveyResponses = surveyResponsesSnapshot.size;

      return { activeInactiveSurveys, recentSurveys, surveyResponses };
    } catch (error) {
      console.error('Error fetching surveys data:', error);
      return { activeInactiveSurveys: { active: 0, inactive: 0 }, recentSurveys: [], surveyResponses: 0 };
    }
  };

  // Chart configurations with green pastel colors
  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true
        }
      }
    }
  };

  const productCategoriesData = {
    labels: ['Uniformes', 'Material', 'Libros', 'Otros'],
    datasets: [{
      data: [
        stats.productCategories.uniforme, 
        stats.productCategories.material, 
        stats.productCategories.libros, 
        stats.productCategories.otros
      ],
      backgroundColor: [COLORS.primary, COLORS.secondary, COLORS.accent, COLORS.success],
      borderColor: [COLORS.accent, COLORS.accent, COLORS.accent, COLORS.accent],
      borderWidth: 2,
    }],
  };

  const serviceEnrollmentsData = {
    labels: stats.serviceEnrollments.map(service => service.name),
    datasets: [{
      label: 'Inscripciones',
      data: stats.serviceEnrollments.map(service => service.count),
      backgroundColor: COLORS.secondary,
      borderColor: COLORS.accent,
      borderWidth: 2,
    }],
  };

  const eventCategoriesData = {
    labels: ['General', 'Cultural', 'Deportivo', 'Educativo', 'Familiar', 'Festivo'],
    datasets: [{
      data: [
        stats.eventCategories.general,
        stats.eventCategories.cultural,
        stats.eventCategories.deportivo,
        stats.eventCategories.educativo,
        stats.eventCategories.familiar,
        stats.eventCategories.festivo
      ],
      backgroundColor: [
        COLORS.primary, 
        COLORS.secondary, 
        COLORS.accent, 
        COLORS.success, 
        COLORS.warning, 
        COLORS.info
      ],
      borderColor: COLORS.accent,
      borderWidth: 2,
    }],
  };

  const surveyStatusData = {
    labels: ['Activas', 'Inactivas'],
    datasets: [{
      data: [stats.activeInactiveSurveys.active, stats.activeInactiveSurveys.inactive],
      backgroundColor: [COLORS.success, '#E0E0E0'],
      borderColor: [COLORS.accent, '#BDBDBD'],
      borderWidth: 2,
    }],
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Fecha desconocida';
    try {
      const date = timestamp instanceof Timestamp ? 
        timestamp.toDate() : 
        new Date(timestamp.seconds * 1000);
      return date.toLocaleDateString('es-ES');
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  const getEventCategoryInfo = (category) => {
    const categories = {
      general: { name: 'General', color: COLORS.primary },
      cultural: { name: 'Cultural', color: COLORS.secondary },
      deportivo: { name: 'Deportivo', color: COLORS.accent },
      educativo: { name: 'Educativo', color: COLORS.success },
      familiar: { name: 'Familiar', color: COLORS.warning },
      festivo: { name: 'Festivo', color: COLORS.info }
    };
    return categories[category] || categories.general;
  };

  // KPI card compacto
  const KpiCard = ({ icon, count, label, route, color }) => (
    <Grid item xs={6} sm={3}>
      <Card onClick={() => navigate(route)} sx={{
        cursor: 'pointer', transition: 'all .2s',
        '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 }
      }}>
        <CardContent sx={{ p: 2, pb: '16px !important' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Avatar sx={{ bgcolor: color + '18', color, width: 36, height: 36 }}>{icon}</Avatar>
            <ArrowForwardIcon sx={{ color: 'text.disabled', fontSize: 14 }} />
          </Box>
          <Typography variant="h4" fontWeight={800} sx={{ color, lineHeight: 1 }}>{count}</Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>{label}</Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: COLORS.accent }} size={60} />
      </Box>
    );
  }

  const today = new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const todayCapitalized = today.charAt(0).toUpperCase() + today.slice(1);

  return (
    <Box sx={{ width: '100%', minHeight: '100vh' }}>

      {/* ── Cabecera ── */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={800} color="text.primary">Panel de Control</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>{todayCapitalized}</Typography>
      </Box>

      {/* ── Acciones rápidas ── */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        <Button variant="contained" size="small" startIcon={<AddIcon />}
          onClick={() => navigate('/admin/events/new')} sx={{ fontWeight: 600 }}>
          Nuevo evento
        </Button>
        <Button variant="outlined" size="small" startIcon={<AddIcon />}
          onClick={() => navigate('/admin/notifications')}>
          Nueva notificación
        </Button>
        <Button variant="outlined" size="small" startIcon={<AddIcon />}
          onClick={() => navigate('/admin/products/new')}>
          Nuevo producto
        </Button>
      </Stack>

      {/* ── KPI strip ── */}
      <Grid container spacing={2} sx={{ mb: 3, width: '100%', mx: 0 }}>
        <KpiCard icon={<PeopleIcon />}        count={stats.usersCount}         label="Familias"        route="/admin/users"          color={SECTION_COLORS.users} />
        <KpiCard icon={<ChildCareIcon />}     count={stats.childrenCount}      label="Alumnos"         route="/admin/children"        color={SECTION_COLORS.children} />
        <KpiCard icon={<EventIcon />}         count={stats.eventsCount}        label="Eventos"         route="/admin/events"          color={SECTION_COLORS.events} />
        <KpiCard icon={<NotificationsIcon />} count={stats.notificationsCount} label="Notificaciones"  route="/admin/notifications"   color={SECTION_COLORS.notifications} />
        <KpiCard icon={<ShoppingCartIcon />}  count={stats.productsCount}      label="Productos"       route="/admin/products"        color={SECTION_COLORS.products} />
        <KpiCard icon={<ReceiptIcon />}       count={stats.ordersCount}        label="Pedidos"         route="/admin/orders"          color={SECTION_COLORS.orders} />
        <KpiCard icon={<SchoolIcon />}        count={stats.servicesCount}      label="Servicios"       route="/admin/services"        color={SECTION_COLORS.services} />
        <KpiCard icon={<PollIcon />}          count={stats.surveysCount}       label="Encuestas"       route="/admin/surveys"         color={SECTION_COLORS.surveys} />
      </Grid>

      {/* ── Contenido principal ── */}
      <Grid container spacing={3} sx={{ width: '100%', mx: 0 }}>

        {/* ════ Columna de actividad (8/12) ════ */}
        <Grid item xs={12} lg={8}>

          {/* Próximos eventos */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} color="text.secondary">PRÓXIMOS EVENTOS</Typography>
              <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/admin/events')}>Ver todos</Button>
            </Box>
            {stats.recentEvents.length === 0 ? (
              <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>Sin eventos próximos</Typography>
            ) : stats.recentEvents.map((event, i) => {
              const cat = getEventCategoryInfo(event.categoria);
              return (
                <Box key={event.id} onClick={() => navigate(`/admin/events/${event.id}`)} sx={{
                  display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 1, cursor: 'pointer',
                  borderBottom: i < stats.recentEvents.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider', borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' }
                }}>
                  <Avatar sx={{ bgcolor: cat.color + '20', color: cat.color, width: 38, height: 38, flexShrink: 0 }}>
                    <EventIcon fontSize="small" />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{event.titulo}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatDate(event.fechaInicio)}</Typography>
                  </Box>
                  <Chip label={cat.name} size="small"
                    sx={{ bgcolor: cat.color, color: '#fff', fontSize: 11, fontWeight: 600, flexShrink: 0 }} />
                </Box>
              );
            })}
          </Paper>

          {/* Pedidos + Notificaciones en paralelo */}
          <Grid container spacing={2} sx={{ mb: 3 }}>

            {/* Pedidos recientes */}
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} color="text.secondary">PEDIDOS</Typography>
                  <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/admin/orders')}>Ver</Button>
                </Box>
                {stats.recentOrders.length === 0 ? (
                  <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>Sin pedidos recientes</Typography>
                ) : stats.recentOrders.map((order, i) => (
                  <Box key={order.id} onClick={() => navigate(`/admin/orders/${order.id}`)} sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    py: 1.25, cursor: 'pointer', borderRadius: 1,
                    borderBottom: i < stats.recentOrders.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' }, px: 0.5
                  }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>#{order.id.slice(0, 6)}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatDate(order.createdAt)}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" fontWeight={700}>{order.total?.toFixed(2) || '0.00'} €</Typography>
                      <Chip label={order.status || 'Pendiente'} size="small" color="warning" variant="outlined"
                        sx={{ height: 18, fontSize: 10 }} />
                    </Box>
                  </Box>
                ))}
              </Paper>
            </Grid>

            {/* Notificaciones recientes */}
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} color="text.secondary">NOTIFICACIONES</Typography>
                  <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/admin/notifications')}>Ver</Button>
                </Box>
                {stats.recentNotifications.length === 0 ? (
                  <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>Sin notificaciones</Typography>
                ) : stats.recentNotifications.map((n, i) => (
                  <Box key={n.id} onClick={() => navigate(`/admin/notifications/${n.id}`)} sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25, cursor: 'pointer',
                    borderRadius: 1, borderBottom: i < stats.recentNotifications.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' }, px: 0.5
                  }}>
                    <Avatar sx={{ bgcolor: '#bf360c18', color: '#bf360c', width: 30, height: 30, flexShrink: 0, mt: 0.3 }}>
                      <NotificationsIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={500} noWrap>{n.título}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatDate(n.fecha)} · {n.tipo || 'General'}</Typography>
                    </Box>
                  </Box>
                ))}
              </Paper>
            </Grid>
          </Grid>

          {/* Encuestas recientes */}
          {stats.recentSurveys.length > 0 && (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} color="text.secondary">ENCUESTAS</Typography>
                <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/admin/surveys')}>Ver todas</Button>
              </Box>
              {stats.recentSurveys.map((survey, i) => (
                <Box key={survey.id} onClick={() => navigate(`/admin/surveys/${survey.id}`)} sx={{
                  display: 'flex', alignItems: 'center', gap: 2, py: 1.25, cursor: 'pointer',
                  borderRadius: 1, borderBottom: i < stats.recentSurveys.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' }, px: 0.5
                }}>
                  <Avatar sx={{ bgcolor: '#880e4f18', color: '#880e4f', width: 30, height: 30, flexShrink: 0 }}>
                    <PollIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                  <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }} noWrap>{survey.título}</Typography>
                  <Chip
                    icon={survey.active ? <CheckCircleIcon sx={{ fontSize: '14px !important' }} /> : <CancelIcon sx={{ fontSize: '14px !important' }} />}
                    label={survey.active ? 'Activa' : 'Inactiva'}
                    size="small" color={survey.active ? 'success' : 'default'} />
                </Box>
              ))}
            </Paper>
          )}
        </Grid>

        {/* ════ Sidebar de charts (4/12) ════ */}
        <Grid item xs={12} lg={4}>

          {/* Productos por categoría */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
              PRODUCTOS POR CATEGORÍA
            </Typography>
            <Box sx={{ height: 220 }}>
              <Pie data={productCategoriesData} options={chartOptions} />
            </Box>
          </Paper>

          {/* Eventos por categoría */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
              EVENTOS POR CATEGORÍA
            </Typography>
            <Box sx={{ height: 220 }}>
              <Pie data={eventCategoriesData} options={chartOptions} />
            </Box>
          </Paper>

          {/* Servicios — inscripciones */}
          {stats.serviceEnrollments.length > 0 && (
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                INSCRIPCIONES POR SERVICIO
              </Typography>
              <Box sx={{ height: 200 }}>
                <Bar data={serviceEnrollmentsData} options={{
                  ...chartOptions,
                  scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
                }} />
              </Box>
            </Paper>
          )}

          {/* Encuestas — resumen compacto */}
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
              ESTADO ENCUESTAS
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'success.50', borderRadius: 1.5 }}>
                  <Typography variant="h4" fontWeight={800} color="success.main">{stats.activeInactiveSurveys.active}</Typography>
                  <Typography variant="caption" color="text.secondary">Activas</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'grey.100', borderRadius: 1.5 }}>
                  <Typography variant="h4" fontWeight={800} color="text.secondary">{stats.activeInactiveSurveys.inactive}</Typography>
                  <Typography variant="caption" color="text.secondary">Inactivas</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#880e4f18', borderRadius: 1.5 }}>
                  <Typography variant="h4" fontWeight={800} sx={{ color: '#880e4f' }}>{stats.surveyResponses}</Typography>
                  <Typography variant="caption" color="text.secondary">Respuestas</Typography>
                </Box>
              </Grid>
            </Grid>
            <Button fullWidth variant="outlined" size="small" onClick={() => navigate('/admin/surveys')}
              endIcon={<ArrowForwardIcon />}>
              Gestionar encuestas
            </Button>
          </Paper>

        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;